import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faUser } from '@fortawesome/free-solid-svg-icons';
import { UserService } from '../../Services/UserService';
import { Utils } from '../../Services/Utils';
import { UserContext } from '../../Contexts';
import { LoginScene, AuthCard } from './LoginScene';

const LoginComponent: React.FC = () => {
    useEffect(() => {
        Utils.setDocumentTitle('Prihlásenie');
    }, []);

    const { login, logout, user } = UserService();
    const { setCurrentUser }: any = useContext(UserContext);
    const navigate = useNavigate();

    const [authChecked, setAuthChecked] = useState(false);
    const [loggedInEmail, setLoggedInEmail] = useState<string | null>(null);

    useEffect(() => {
        user().then(res => {
            if (res && Utils.isUserLoggedIn(res.id)) {
                setLoggedInEmail(res.email);
                setCurrentUser(res);
            }
        }).catch(() => {}).finally(() => setAuthChecked(true));
    }, []);

    const [isLogging, setIsLogging] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [returnUrl, setReturnUrl] = useState('/home');
    const nonReturnableUrls: string[] = ['/401', '/404'];

    const location = useLocation();
    useEffect(() => {
        const state = location.state as { returnUrl?: string } | null;
        const url = state?.returnUrl ?? '/home';
        setReturnUrl(url.endsWith('/401') || url.endsWith('/404') ? '/home' : url);
    }, [location.state]);

    const handleLogout = async () => {
        await logout();
        setLoggedInEmail(null);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsLogging(true);
        login(email, password)
            .then(() => user())
            .then(res => {
                if (res && Utils.isUserLoggedIn(res.id)) {
                    setCurrentUser(res);
                }
                const target = nonReturnableUrls.includes(returnUrl) ? '/home' : returnUrl;
                navigate(target, { replace: true });
            })
            .catch(() => {})
            .finally(() => setIsLogging(false));
    };

    return (
        <LoginScene>
            {authChecked && (
                <AuthCard title={loggedInEmail ? 'Ste prihlásený' : 'Prihláste sa'}>
                    {loggedInEmail ? (
                        /* ── Already logged in ── */
                        <div>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                padding: '0.75rem 1rem',
                                borderRadius: '10px',
                                backgroundColor: 'var(--st-paper-tint)',
                                border: '1.5px solid var(--st-line)',
                                marginBottom: '1.5rem',
                            }}>
                                <div style={{
                                    width: 34, height: 34,
                                    borderRadius: '50%',
                                    backgroundColor: 'var(--st-ink)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                    <FontAwesomeIcon icon={faUser} style={{ color: 'white', fontSize: '0.75rem' }} />
                                </div>
                                <div style={{ minWidth: 0 }}>
                                    <div className="st-eyebrow" style={{ color: 'var(--st-text-faint)', fontSize: '0.58rem' }}>
                                        Prihlásený ako
                                    </div>
                                    <div style={{ fontSize: '0.875rem', color: 'var(--st-text)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {loggedInEmail}
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={() => navigate(returnUrl, { replace: true })}
                                className="st-btn st-btn--fill"
                                style={{ width: '100%', marginBottom: '0.6rem' }}
                            >
                                Pokračovať
                            </button>

                            <button
                                onClick={handleLogout}
                                className="st-btn st-btn--outline"
                                style={{ width: '100%' }}
                            >
                                Odhlásiť sa
                            </button>
                        </div>
                    ) : (
                        /* ── Login form ── */
                        <form onSubmit={handleSubmit}>

                            {/* Email */}
                            <div style={{ marginBottom: '1rem' }}>
                                <label className="st-label" htmlFor="login-email">
                                    Email
                                </label>
                                <input
                                    id="login-email"
                                    type="email"
                                    required
                                    autoFocus
                                    disabled={isLogging}
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    placeholder="vas@email.com"
                                    className="st-input"
                                />
                            </div>

                            {/* Password */}
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label className="st-label" htmlFor="login-password">
                                    Heslo
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        id="login-password"
                                        type={showPassword ? 'text' : 'password'}
                                        required
                                        disabled={isLogging}
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        className="st-input"
                                        style={{ paddingRight: '2.5rem' }}
                                    />
                                    <button
                                        type="button"
                                        tabIndex={-1}
                                        onClick={() => setShowPassword(p => !p)}
                                        aria-label={showPassword ? 'Skryť heslo' : 'Zobraziť heslo'}
                                        style={{
                                            position: 'absolute',
                                            right: '0.75rem',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: 'var(--st-text-faint)',
                                            padding: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} style={{ fontSize: '0.85rem' }} />
                                    </button>
                                </div>
                            </div>

                            {/* Submit */}
                            <button
                                type="submit"
                                disabled={isLogging}
                                className="st-btn st-btn--fill"
                                style={{ width: '100%', marginBottom: '1.1rem' }}
                            >
                                {isLogging ? (
                                    <>
                                        <span style={{
                                            width: 14, height: 14,
                                            border: '2px solid rgba(255,255,255,0.3)',
                                            borderTopColor: 'white',
                                            borderRadius: '50%',
                                            animation: 'loginSpin 0.65s linear infinite',
                                            display: 'inline-block',
                                            flexShrink: 0,
                                        }} />
                                        Prihlasujem...
                                    </>
                                ) : 'Prihlásiť sa'}
                            </button>

                            <div style={{ textAlign: 'center' }}>
                                <Link to="/forgottenpassword" style={{ fontSize: '0.78rem', color: 'var(--st-text-faint)', textDecoration: 'none' }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = 'var(--st-accent)'; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'var(--st-text-faint)'; }}
                                >
                                    Zabudnuté heslo?
                                </Link>
                            </div>

                        </form>
                    )}
                </AuthCard>
            )}
        </LoginScene>
    );
};

export default LoginComponent;
